// ============================================================================
// ui/help-assist.js — the in-tool AI help assistant (PUBLIC edition only)
// ============================================================================
// A direct Q&A panel over the WD help corpus: the buyer asks about any feature
// and gets a device-aware answer from chatbot-worker's topic:'weight-designer'
// mode (Haiku, its own budget lanes — see chatbot-worker/src/wd-help.js).
// Deliberately NOT a persona chat — this is the manual with a search box that
// talks back, not the storefront's Jake.
//
// SURFACES:
//   desktop — a "?" button in the top-right tool cluster (joins the chrome
//             idle-fade like its neighbours); the panel is a flat-glass card
//             pinned to the viewport's bottom-right.
//   phone   — a "Help & questions" row in the full-screen hamburger; the same
//             panel rendered FULL-SCREEN.
//
// ⭐ IT IS SHAPED AFTER THE STOREFRONT CHAT (Ryan 2026-08-09). Same window size,
// same header/welcome/bubble/composer/footer anatomy, same full-screen phone
// behaviour — a buyer who has used the site's chat should recognise this on
// sight. Only the palette differs (dark app, dark card). If you restyle either
// one, look at `assets/custom-chatbot.css` before inventing a value here.
//
// STACKING: the panel is BODY-APPENDED with position:fixed, never nested in
// the canvas — the full-screen mode root is z 2147483000 and the burger
// 2147483001 (see mobile-launch.js), so the panel sits at 2147483002 and no
// ancestor transform/stacking context can swallow it (the burger learned this
// the hard way — memory `wd-fullscreen-chrome-tokens-need-root`). All styles
// are plain values under .wd-help-*, no --dt tokens, for the same reason.
//
// REP MODE: never constructed — main.js guards with isRepMode(). Rep demos
// don't need buyer help, and portal pages keep the Team-AI widget instead.
// ============================================================================

import { h } from './dom.js';
import { isPhone, isFullScreenOpen } from './mobile-launch.js';
import { observeKeyboard } from './keyboard-inset.js';

const CHATBOT_WORKER_URL = 'https://chatbot-worker.ryan-29b.workers.dev';
const PUBLIC_TOOL_PATH = '/pages/weight-designer';
const SID_KEY = 'wd:help:sid';
const INPUT_MAX = 500;

export const ICON_HELP = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9.2 9a2.9 2.9 0 0 1 5.6 1c0 1.8-2.6 2.2-2.6 3.6"/><circle cx="12" cy="17.3" r="0.4" fill="currentColor"/></svg>';

/* THE SAME MARK THE STOREFRONT CHAT WEARS (`snippets/custom-chatbot-widget.liquid`),
   deliberately the identical CDN URL rather than a bundled copy: this panel is meant
   to be recognised as the same assistant people already use on the site, and two
   files that must stay in step are one file too many. */
const BRAND_MARK = 'https://cdn.shopify.com/s/files/1/0684/9350/8844/files/Favicon_Logo_-_JPEG.jpg?v=1774975597';

/* Ryan 2026-08-09, replacing the step-aware starter chips: *"get rid of them.
   Instead, just include…"*. Verbatim — it is customer copy. The chips were free
   (no API call until tapped) but they answered a question nobody had asked, and
   they made the panel look like a menu rather than a place to type. */
const WELCOME_TEXT = 'Ask me any question about the Custom Weight Designer and I’ll do my best to assist you!';

function sessionId() {
  try {
    let sid = sessionStorage.getItem(SID_KEY);
    if (!sid) {
      sid = 'wdh_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      sessionStorage.setItem(SID_KEY, sid);
    }
    return sid;
  } catch { return 'wdh_ephemeral'; }
}

/* ── the reply renderer: escape everything, then re-admit exactly three things ────────────────
 * **bold** (the one bit of markdown the system prompt asks for), markdown links, and BARE
 * addresses — a URL, an email or a phone number typed as plain text.
 *
 * ⛔ THE BARE CASE IS THE ONE THAT MATTERS, and it is why this grew (Ryan 2026-08-09, testing on
 * localhost): *"I asked 'I need help from a person' and it referenced the Contact page in bold,
 * but it wasn't hyperlinked."* The corpus entry it was quoting DOES carry a real `<a>` — but
 * `build-help.mjs`'s `strip()` removes every tag on the way to the assistant, deliberately, so
 * what the model reads is the address as words. It then repeats it as words. Every route out of
 * this panel therefore arrived as dead text: the contact form, the phone number and the email.
 *   The storefront chat has the same renderer shape and handles `[label](url)` only, because its
 * prompt asks for markdown links. Both forms are accepted here rather than betting on the model
 * choosing one.
 *
 * ⚠ ORDER IS LOAD-BEARING. Escape first (nothing below may see raw markup). Then markdown links,
 * so a URL inside one is already an `<a>` before the autolinker runs. Then autolink, SPLIT AROUND
 * the anchors that now exist — a global replace over the whole string would happily match the
 * address inside an `href` it had just written and nest an anchor in an anchor. Bold goes last
 * because `[^*]+` spans an anchor harmlessly, so `**<a…>…</a>**` wraps rather than breaks.
 *   `white-space: pre-wrap` on `.wd-help-msg` already renders newlines, so there is no `<br>` pass.
 */
const MD_LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
/* One alternation, one pass: email | url | phone. Emails come FIRST because the url branch would
   otherwise claim the domain half of one and leave "info@" stranded in front of a link.
   The url branch also accepts a SCHEMELESS blueskyfitsupply.com address, because that is exactly
   the shape the corpus uses as its link text and therefore the shape the model repeats. */
const AUTOLINK_RE = new RegExp([
  '([A-Za-z0-9._%+-]+@[A-Za-z0-9-]+\\.[A-Za-z0-9.-]+)',
  /* ⚠ `*` IS EXCLUDED FROM THE URL'S CHARACTERS, and it is not a nicety. The system prompt asks
     the model to bold what it names, so the single likeliest thing it writes is
     `**https://www.blueskyfitsupply.com/pages/contact**` — and a greedy class swallows the closing
     `**` into the address, which yields a broken href AND leaves an unmatched `**` for the bold
     pass to wrap around half an anchor. Caught by test/render-reply, not by reading. */
  '((?:https?:\\/\\/|www\\.)[^\\s<>"\'*]+|(?:[a-z0-9-]+\\.)*blueskyfitsupply\\.com(?:\\/[^\\s<>"\'*]*)?)',
  '(\\(?\\d{3}\\)?[-.\\s]\\d{3}[-.\\s]\\d{4})',
].join('|'), 'gi');

/* ── AND THE PHRASE ITSELF IS A LINK, because the model will not always type the address ──────
 * Ryan 2026-08-09, on the second round of this: *"It just bolded 'contact page' again but there
 * was no link on the words 'contact page'. I want these words to be linked and underlined!"*
 *
 * The prompt asks for all three routes WITH the address, and the model complied about half the
 * time. Autolinking only what it typed therefore left the most natural phrasing — "use our contact
 * page" — as dead words, which is the same dead end as before wearing better clothes. So the
 * destination is known here rather than hoped for from the reply: whatever it writes, the words
 * become the link.
 *
 * ⚠ FIRST MATCH ONLY, and skipped entirely when the reply already carries a contact-URL link — a
 * sentence naming the page AND its address should get one link, not two side by side.
 * ⚠ `page|form|us` is required after "contact", which is what keeps it off "someone will contact
 * me after I submit" and every other innocent use of the verb.
 */
const CONTACT_URL = 'https://www.blueskyfitsupply.com/pages/contact';
const CONTACT_PHRASE_RE = /\b(?:the |our |your |site'?s )?contact (?:us )?(?:page|form)\b|\bcontact us\b/i;
/* Splits a string into anchors and everything else. Used by both passes below so neither can write
   a link inside a link — the failure mode of a plain global replace over markup you just made. */
const ANCHOR_SPLIT = /(<a\b[^>]*>.*?<\/a>)/g;

const anchor = (href, label) =>
  `<a href="${href}" target="_blank" rel="noopener">${label}</a>`;

function autolink(chunk) {
  return chunk.replace(AUTOLINK_RE, (match, email, url, phone) => {
    if (email) return anchor(`mailto:${email}`, email);
    if (phone) return anchor(`tel:${phone.replace(/\D/g, '')}`, phone);
    /* ⚠ TRAILING PUNCTUATION IS NOT PART OF THE URL. "…/pages/contact." ends a sentence far more
       often than it names a path, and swallowing the full stop makes the link both wrong and
       visibly odd. Handed back outside the anchor. */
    const trail = url.match(/[.,;:!?)]+$/);
    const clean = trail ? url.slice(0, -trail[0].length) : url;
    const href = /^https?:\/\//i.test(clean) ? clean : `https://${clean}`;
    return anchor(href, clean) + (trail ? trail[0] : '');
  });
}

/* EXPORTED for `test/help-render.smoke.mjs` and for nothing else. This function is pure and it is
   the only place a model's text becomes markup in this app, which makes it exactly the thing to
   pin with assertions rather than by opening the panel and looking. */
export function renderReply(text) {
  let html = String(text || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  html = html.replace(MD_LINK_RE, (_, label, url) => anchor(url, label));
  html = html
    .split(ANCHOR_SPLIT)
    .map((part) => (part.startsWith('<a ') ? part : autolink(part)))
    .join('');
  // …then the phrase, but only when the address itself did not already become a link.
  if (!/href="[^"]*\/pages\/contact/.test(html)) {
    let linked = false;
    html = html
      .split(ANCHOR_SPLIT)
      .map((part) => {
        if (linked || part.startsWith('<a ')) return part;
        // Non-global regex: replaces the FIRST match in this chunk and no more.
        return part.replace(CONTACT_PHRASE_RE, (m) => { linked = true; return anchor(CONTACT_URL, m); });
      })
      .join('');
  }
  return html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
}

/**
 * Can this guide deep-link be answered by scrolling THIS page, or does it need a new tab?
 *
 * ⭐ SCROLLING IS THE ANSWER WHENEVER IT IS POSSIBLE (Ryan 2026-08-09: *"when I click one of those
 * links at the bottom, can it just scroll me down to the question/answer below while keeping me on
 * the same tab?"*). The Help & Guide is on the storefront page already, so a new tab would be a
 * cold boot of the whole designer to read a paragraph that is four screens below.
 *
 * ⚠ ASKED AT CLICK TIME, NOT AT RENDER TIME, and that is the fix rather than a detail. The old
 * version decided when the sources block was BUILT and baked the answer into the `href`. Full-screen
 * mode can open or close between a reply arriving and the buyer tapping it, so the decision was made
 * against a layout that no longer existed.
 *
 * Three conditions, all of which must hold:
 *   · we are ON the tool's storefront page (a standalone host like pages.dev has no guide to scroll to);
 *   · the target is on that same page;
 *   · ⛔ the tool is NOT full-screen. On a phone the designer covers the viewport, so scrolling the
 *     page underneath is a link that visibly does nothing. That case gets the new tab, deliberately:
 *     exiting full-screen to read one answer would cost the buyer the design they are standing in,
 *     and the new tab is now a clean landing (see `kbAnchorVisit` in main.js, which stops the resume
 *     gate firing on a `#wd-kb-` arrival).
 *
 * @returns {string|null} the `#wd-kb-…` hash to jump to, or null to let the link open a new tab.
 */
/* EXPORTED for test/help-render.smoke.mjs. Pure apart from two globals it reads, both stubbable. */
export function sameGuidePage(url) {
  try {
    if (isFullScreenOpen()) return null;
    if (!location.pathname.startsWith(PUBLIC_TOOL_PATH)) return null;
    const u = new URL(url, location.href);
    if (!u.pathname.startsWith(PUBLIC_TOOL_PATH)) return null;
    return u.hash || null;
  } catch { return null; }
}

/**
 * @param {object} opts
 * @param {() => number} opts.getStep         current step (1-4)
 * @param {() => string[]} opts.getFamilies   picked category ids
 * @param {() => boolean} [opts.isFullscreen]
 * @param {(open:boolean) => void} [opts.onToggle]  reflect state on the launcher
 * @param {() => Promise<string|null>} [opts.getShareLink]  a permanent URL for the live design,
 *   used by the hand-off so a rep opens the exact plate the buyer was stuck on
 */
export function createHelpAssist(opts) {
  const { getStep, getFamilies, isFullscreen, onToggle, getShareLink } = opts;

  let openState = false;
  let busy = false;
  /* Once a hand-off lead is in, the offer never comes back in this session. The worker enforces
     the same thing (it will answer `already: true` rather than post twice), so this is the polite
     half of a rule that is also structurally guaranteed. */
  let handoffDone = false;
  let handoffCard = null;   // the live card, so a second offer replaces rather than stacks

  /* THE WELCOME STATE IS A CHILD OF THE MESSAGE LIST, not a sibling, and that is
     load-bearing: `.wd-help-welcome:not(:only-child) { display: none }` retires it
     the moment a first message lands, with no JS to forget to call. Straight from
     the storefront chat, which solves the same problem the same way. */
  const welcome = h('div', { class: 'wd-help-welcome' },
    h('img', { class: 'wd-help-welcome-icon', src: BRAND_MARK, alt: '', width: '48', height: '48', loading: 'lazy' }),
    h('p', { class: 'wd-help-welcome-text' }, WELCOME_TEXT));

  const msgList = h('div', { class: 'wd-help-msgs', role: 'log', 'aria-live': 'polite', 'aria-label': 'Help conversation' }, welcome);
  const input = h('textarea', {
    class: 'wd-help-input', rows: '1', maxlength: String(INPUT_MAX),
    placeholder: 'Type a message...', 'aria-label': 'Ask a question about the Weight Designer',
  });
  const sendBtn = h('button', { type: 'submit', class: 'wd-help-send', 'aria-label': 'Send' },
    h('span', { 'aria-hidden': 'true', html: '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>' }));
  const closeBtn = h('button', { type: 'button', class: 'wd-help-close', 'aria-label': 'Close help' },
    h('span', { 'aria-hidden': 'true', html: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' }));

  const panel = h('aside', { class: 'wd-help-panel wd-flat-glass', role: 'dialog', 'aria-label': 'Weight Designer help', hidden: true },
    h('header', { class: 'wd-help-head' },
      h('div', { class: 'wd-help-head-info' },
        h('img', { class: 'wd-help-head-logo', src: BRAND_MARK, alt: '', width: '28', height: '28', loading: 'lazy' }),
        h('div', { class: 'wd-help-head-id' },
          h('span', { class: 'wd-help-head-name' }, 'Weight Designer Help'),
          h('span', { class: 'wd-help-head-badge' }, 'ONLINE'))),
      closeBtn),
    msgList,
    h('form', { class: 'wd-help-inputrow', onSubmit: (e) => { e.preventDefault(); void ask(input.value); } }, input, sendBtn),
    h('div', { class: 'wd-help-footer' },
      h('div', { class: 'wd-help-footer-powered' },
        h('span', { class: 'wd-help-footer-label' }, 'Powered by'),
        h('span', { class: 'wd-help-footer-brand' }, 'Blue Sky Fitness Supply'))));
  document.body.appendChild(panel);

  function addMsg(role, html) {
    const row = h('div', { class: `wd-help-msg wd-help-msg--${role}` });
    row.innerHTML = html;
    msgList.appendChild(row);
    msgList.scrollTop = msgList.scrollHeight;
    return row;
  }

  /* ── THE STUCK-USER HAND-OFF (Ryan 2026-08-09, Q10 of the corpus audit) ──────────────────────
   * *"how does the tool handle it if the person is lost, genuinely can't get any further and
   * needs help? … we need a solution of some kind."*
   *
   * Not a contact form and not a second inbox: the same bridge the storefront chat uses. Name and
   * email go to chatbot-worker, which posts a real lead to rfq-worker's `/lead-intake` carrying
   * this conversation and a link to the design. It lands in the Deal Feed beside every other
   * weight lead, so the rep who calls back has already read where the buyer got stuck and can
   * open the exact plate.
   *
   * ⚠ IT LIVES INSIDE THE MESSAGE LIST, not as a modal or a footer. A buyer who is already lost
   * is the last person to hand a new surface to, and a card in the thread reads as the assistant
   * answering rather than as the app changing shape. It also scrolls away naturally when ignored.
   *
   * ⚠ THE SHARE LINK IS FETCHED AT SUBMIT, not when the card appears. Warming it is a worker
   * round trip, and an offer that fires on "I'm stuck" would otherwise POST a design record for
   * every buyer who reads the card and closes it.
   */
  function showHandoff() {
    if (handoffDone) return;
    if (handoffCard && handoffCard.isConnected) return;   // already on screen, do not stack

    const nameInput = h('input', {
      class: 'wd-help-ho-input', type: 'text', autocomplete: 'name', maxlength: '120',
      placeholder: 'Your name', 'aria-label': 'Your name',
    });
    const emailInput = h('input', {
      class: 'wd-help-ho-input', type: 'email', autocomplete: 'email', maxlength: '200',
      inputmode: 'email', placeholder: 'Your email', 'aria-label': 'Your email',
    });
    const err = h('p', { class: 'wd-help-ho-err', role: 'status', hidden: true });
    const submit = h('button', { type: 'submit', class: 'wd-help-ho-go' }, 'Send it over');

    const form = h('form', { class: 'wd-help-ho-form', onSubmit: (e) => { e.preventDefault(); void send(); } },
      nameInput, emailInput, submit);

    const card = h('div', { class: 'wd-help-handoff' },
      h('p', { class: 'wd-help-ho-t' }, 'Want someone to take a look?'),
      h('p', { class: 'wd-help-ho-s' }, 'Leave your name and email. Your question, this conversation and a link to your design go over together, so nobody asks you to explain it twice.'),
      form, err);
    handoffCard = card;
    msgList.appendChild(card);
    msgList.scrollTop = msgList.scrollHeight;

    const fail = (msg) => {
      err.textContent = msg;
      err.hidden = false;
      submit.disabled = false;
      submit.textContent = 'Send it over';
    };

    async function send() {
      if (submit.disabled) return;
      err.hidden = true;
      const name = nameInput.value.trim();
      const email = emailInput.value.trim();
      if (!name) { fail('Add your name so we know who to ask for.'); nameInput.focus(); return; }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { fail('That email does not look right.'); emailInput.focus(); return; }
      submit.disabled = true;
      submit.textContent = 'Sending…';

      /* A design link is a bonus, not a requirement. Someone stuck on step 1 may have nothing worth
         storing, and `warmShareLink` can fail on a bad connection — neither is a reason to lose the
         lead, so it is caught to null and the worker prints "(no design link)" for the rep. */
      let shareUrl = null;
      try { shareUrl = getShareLink ? await getShareLink() : null; } catch { shareUrl = null; }

      try {
        const resp = await fetch(CHATBOT_WORKER_URL + '/wd-help/handoff', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: sessionId(),
            name, email,
            shareUrl: shareUrl || undefined,
            context: {
              device: isPhone() ? 'phone' : 'desktop',
              step: getStep(),
              families: (getFamilies() || []).join(','),
            },
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data.ok) { fail(data.error || 'Could not send that just now. Try again, or use Request Quote on step 4.'); return; }
      } catch {
        fail('No connection. Try again, or use Request Quote on step 4.');
        return;
      }

      handoffDone = true;
      /* The card becomes its own confirmation rather than being removed and replaced by a bot
         bubble: the thing the buyer just filled in should be the thing that tells them it worked. */
      card.classList.add('is-sent');
      card.replaceChildren(
        h('p', { class: 'wd-help-ho-t' }, 'Sent. Someone will be in touch.'),
        h('p', { class: 'wd-help-ho-s' }, `We have your design and this conversation, and follow-up usually lands within one business day at ${email}. Keep designing in the meantime if you like, nothing here is locked.`),
      );
      msgList.scrollTop = msgList.scrollHeight;
    }
  }

  async function ask(question) {
    const q = String(question || '').trim();
    if (!q || busy) return;
    busy = true;
    sendBtn.disabled = true;
    input.value = '';
    addMsg('user', renderReply(q));
    const pending = addMsg('bot', '<span class="wd-help-dots" aria-label="Thinking"><span></span><span></span><span></span></span>');
    try {
      const resp = await fetch(CHATBOT_WORKER_URL + '/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: 'weight-designer',
          message: q,
          sessionId: sessionId(),
          context: {
            device: isPhone() ? 'phone' : 'desktop',
            step: getStep(),
            families: (getFamilies() || []).join(','),
            fullscreen: !!(isFullscreen && isFullscreen()),
            page_url: location.pathname,
          },
        }),
      });
      const data = await resp.json();
      const reply = data.reply || data.error || 'Something went wrong — try again.';
      pending.innerHTML = renderReply(reply);
      /* ⚠ THESE ARE REFERENCES, NOT SUGGESTED QUESTIONS, and they used to read as
         suggestions — the top source IS the entry that answered the question, so the
         first link restated what had just been asked ("How do I upload my logo?"
         under the answer to "how do I upload a logo"). Ryan 2026-08-09 called that
         *"poor UX"*, and he was reading the block exactly as its shape invited. Two
         halves to the fix: the worker now drops any source that is a restatement of
         the question, and the block wears a label that says what it is. */
      if (Array.isArray(data.sources) && data.sources.length) {
        const links = h('div', { class: 'wd-help-sources' },
          h('span', { class: 'wd-help-sources-label' }, 'More in the Help & Guide'),
          /* ⚠ THE `href` IS ALWAYS THE FULL URL, and the same-page jump is a click-time DECISION
             layered over it. That ordering is what makes the link honest in every state: middle-
             click, "open in new tab" and copy-link-address all get a real destination, and a plain
             click still scrolls when scrolling is possible. Building the `href` as a bare `#hash`
             (the old shape) meant those three gestures copied a fragment of the wrong page. */
          data.sources.map((s) => h('a', {
            class: 'wd-help-source', href: s.url, target: '_blank', rel: 'noopener',
            onClick: (e) => {
              const hash = sameGuidePage(s.url);
              if (!hash) return;               // no guide under us — let the new tab open
              e.preventDefault();
              close();
              /* ⛔ SETTING AN IDENTICAL HASH FIRES NOTHING. The KB opens and scrolls its item from
                 `hashchange` (assets/weight-designer-kb.js), so asking twice about the same entry
                 would silently do nothing the second time — the one click most likely to be a
                 buyer who scrolled away and wants to get back to it. Re-fire the event by hand in
                 that case; the KB's handler reads `location.hash` and ignores the event object,
                 so a plain Event is enough. */
              if (location.hash === hash) window.dispatchEvent(new Event('hashchange'));
              else location.hash = hash;
            },
          }, `→ ${s.q}`)));
        pending.appendChild(links);
      }
      msgList.scrollTop = msgList.scrollHeight;
      /* The worker decides, not this file and not the wording of the reply — see the note on
         `offerHandoff` in chatbot-worker/src/wd-help.js for why the flag and the model's sentence
         are deliberately independent. */
      if (data.offerHandoff) showHandoff();
    } catch {
      pending.innerHTML = renderReply('No connection — check your network and try again, or scroll down to the Help & Guide.');
    } finally {
      busy = false;
      sendBtn.disabled = false;
    }
  }

  /* ⭐ THE PANEL DOES NOT MEASURE THE KEYBOARD, AND THAT IS THE FIX — see
     `ui/keyboard-inset.js` for the three attempts that did and why each failed.
     While the panel is open it puts the page on `interactive-widget=resizes-content`,
     the browser shrinks the layout viewport to the real remaining space (including
     Chrome's autofill strip, which nothing we could query knew about), and the CSS is
     plain `height: 100dvh`. Safe here only because a full-screen panel hides the
     canvas that WD 253's keyword was protecting. */
  let unbindKeyboard = null;
  function bindPhoneHeight(on) {
    if (unbindKeyboard) { unbindKeyboard(); unbindKeyboard = null; }
    if (!on) { panel.classList.remove('is-kb'); return; }
    unbindKeyboard = observeKeyboard(({ keyboardOpen }) => {
      /* The POWERED BY strip stands down while typing. ⚠ NOT a bug fix — it was
         once believed to BE the gap, and hiding it changed nothing (Ryan: *"the
         POWERED BY section disappears too… gap seems the same"*). Kept because a
         credit line has nothing to say mid-sentence and the room is worth more. */
      panel.classList.toggle('is-kb', keyboardOpen);
      // The panel just got shorter; without this the last reply scrolls out of
      // sight at the exact moment the keyboard arrives to reply to it.
      msgList.scrollTop = msgList.scrollHeight;
    });
  }

  /* The phone sheet's exit, held so the animation can play — see the note by `wd-help-sheet-out`.
     Cleared by a re-open, which is the case that makes a timer wrong and a listener right. */
  let closingHandler = null;
  function cancelClosing() {
    if (!closingHandler) return;
    panel.removeEventListener('animationend', closingHandler);
    closingHandler = null;
    panel.classList.remove('is-closing');
  }

  /* ── THE BACK GESTURE HAS TO CLOSE THE CHAT, NOT THE APP (Ryan 2026-08-09) ─────────────────────
   * *"If I'm opening the chat menu on mobile, then I swipe to go back, the chat menu stays open but
   * the app closes behind it… then I close the chat menu, have to reopen the app, then the chat menu
   * doesn't pop up when I click the ? button."*
   *
   * ⛔ THE CAUSE IS AN ABSENCE, NOT A HANDLER GONE WRONG. A back swipe pops whatever history entry is
   * on top; the phone takeover owns one, and the chat panel owned none — so back reached straight
   * past the thing covering the screen and closed the app underneath it, leaving an orphaned panel
   * floating over the storefront. Every symptom after that is downstream of the two getting out of
   * step, including the ? that then appears dead.
   *   A sheet that covers the screen must be a history STOP. Push an entry when it opens, close on
   * `popstate`, and consume the entry when it is dismissed any other way, so the stack does not grow
   * an orphan per open.
   *
   * ⚠ PHONE ONLY. On desktop the chat is a card in the corner, not a screen — hijacking the browser's
   * Back button for a 380px popover would be worse than the bug.
   */
  let pushedHistory = false;
  function pushHistoryStop() {
    if (pushedHistory || !isPhone()) return;
    try { window.history.pushState({ wdHelp: 1 }, ''); pushedHistory = true; } catch { /* blocked */ }
  }
  function consumeHistoryStop() {
    if (!pushedHistory) return;
    pushedHistory = false;
    // Ours is the entry on top, so this unwinds exactly what `pushHistoryStop` added.
    try { window.history.back(); } catch { /* blocked */ }
  }
  window.addEventListener('popstate', () => {
    if (!openState) return;
    // The entry we pushed has just been popped — do NOT consume another one on the way out.
    pushedHistory = false;
    close();
  });

  function open() {
    if (openState) return;
    openState = true;
    /* ⚠ RE-ATTACHED IF SOMETHING TOOK IT. The panel is body-appended and outlives any single mount
       of the app; a teardown that clears the body would otherwise leave this instance holding a
       detached node, toggling state nobody can see — which is what "the ? button does nothing"
       looks like from the outside. */
    if (!panel.isConnected) document.body.appendChild(panel);
    pushHistoryStop();
    /* ⚠ AN INTERRUPTED CLOSE HAS TO BE CANCELLED, not waited out. Tapping ? twice quickly would
       otherwise leave the pending `animationend` to hide a panel that has just been re-opened —
       the class is gone by then, but the handler still fires and still sets `hidden`. */
    cancelClosing();
    panel.hidden = false;
    const phone = isPhone();
    panel.classList.toggle('wd-help-panel--sheet', phone);
    bindPhoneHeight(phone);
    if (onToggle) onToggle(true);
    // Focus the input on desktop; on phone let the sheet settle first so the
    // keyboard doesn't fight the open animation.
    if (!isPhone()) input.focus();
  }

  function close() {
    if (!openState) return;
    openState = false;
    consumeHistoryStop();
    /* ⛔ THE KEYBOARD GOES IMMEDIATELY, THE PANEL GOES WHEN IT HAS FINISHED LEAVING. `bindPhoneHeight`
       owns the page's `interactive-widget` keyword while the panel is up (WD 254), and holding that
       through a 400ms exit means the layout viewport is still shrunk to the keyboard on a panel that
       is visibly going away. Unbinding first restores it under the slide, where nothing is looking. */
    bindPhoneHeight(false);
    if (onToggle) onToggle(false);
    /* Desktop is a card, not a sheet: nothing to play, hide now (see the CSS note). */
    if (!panel.classList.contains('wd-help-panel--sheet')) {
      panel.hidden = true;
      return;
    }
    /* ⚠ `animationend`, NOT a matching setTimeout. The duration lives in ONE place — the stylesheet,
       where it is copied from the storefront chat — and a JS timer beside it is a second copy that
       is one restyle away from disagreeing. It also gets `prefers-reduced-motion` for free: the
       media query shortens the animation to 0.01ms and the event simply arrives sooner. */
    cancelClosing();
    closingHandler = () => {
      panel.removeEventListener('animationend', closingHandler);
      closingHandler = null;
      panel.classList.remove('is-closing');
      panel.hidden = true;
    };
    panel.addEventListener('animationend', closingHandler);
    panel.classList.add('is-closing');
  }
  function toggle() { openState ? close() : open(); }

  closeBtn.addEventListener('click', close);
  panel.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  // ⚠ The send button is type="submit" and the composer is a real <form> (the
  // storefront chat's shape) — a click listener here as well would ask twice.
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void ask(input.value); }
  });

  /* THE HAND-OFF FROM THE FAQ BELOW THE TOOL (2026-08-01). A search there that
     matches nothing used to be the one dead end in the whole help loop; now its
     no-results state offers "Ask the assistant" and the query arrives here.
     Announced both ways — a flag for a KB that initialises after us, an event
     for one that initialised first — because the two scripts load independently
     and neither can assume it won the race. The KB keeps its button hidden
     until it hears this, so a blocked bundle never leaves a dead control. */
  const api = { el: panel, open, close, toggle, ask, isOpen: () => openState, offerHandoff: showHandoff };
  window.addEventListener('wd:ask-help', (e) => {
    const q = e?.detail?.question;
    open();
    if (q) void ask(q);
  });
  try {
    window.__wdHelpReady = true;
    window.dispatchEvent(new CustomEvent('wd:help-ready'));
  } catch { /* older browser — the flag alone still works */ }

  return api;
}
